import { Injectable, inject } from "@angular/core";
import { CanActivate, CanActivateFn, Router } from "@angular/router";
import { AgsAuthService } from "@ags/core";
import { firstValueFrom } from "rxjs";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { ApiService } from "../services";
import { IUserProfile } from "../npr-request.model";
import { SharedDataService } from "../services/sharedDataService";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class LandingGuard implements CanActivate {
  constructor(
    private readonly authService: AgsAuthService,
    private readonly loadingScreenService: LoadingScreenService,
  ) { }

  async canActivate() {
    if (await this.authService.getAccount()) {
      await this.authService.acquireToken();
      return true;
    }

    this.loadingScreenService.setLoading(true);
    await this.authService.acquireToken();
    return false;
  }
}

/** Boolean role flags on IUserProfile that can be used to gate route access. */
export type RoleFlag = {
  [K in keyof IUserProfile]: IUserProfile[K] extends boolean ? K : never;
}[keyof IUserProfile];

/**
 * Returns a CanActivateFn that allows the route when the current user profile
 * has ANY of the supplied role flags set to true. Replaces the previous family
 * of IsXxx / HasPermissionToXxx guard classes — Angular's canActivate array is
 * AND-combined, so OR semantics across roles cannot be expressed by listing
 * multiple class guards and must be folded into a single guard.
 *
 * Usage: canActivate: [LandingGuard, hasAnyRole("IsFfpAdmin", "IsAdmin")]
 */
export const hasAnyRole = (...roles: RoleFlag[]): CanActivateFn => () => {
  const apiService = inject(ApiService);
  const sharedDataService = inject(SharedDataService);
  const router = inject(Router);

  const profile$: Promise<IUserProfile | null> = ApiService.userProfile
    ? apiService.getUserProfile()
    : firstValueFrom(sharedDataService.getUserProfile());

  return profile$.then((profile) => {
    const allowed = !!profile && roles.some((r) => !!profile[r]);
    if (!allowed) {
      router.navigate(["create-request"]);
    }
    return allowed;
  });
};

/** FFP view permission additionally bypasses the role check in non-prod builds. */
export const hasPermissionToViewFfp: CanActivateFn = (route, state) =>
  !environment.production ? true : hasAnyRole("IsFfpAdmin", "IsAdmin")(route, state);

/**
 * Allows the route only when the CCO Test page is enabled for the current
 * environment (via environment.js) AND the user is an admin. The feature is
 * environment-scoped, so the flag comes from the environment rather than the
 * user profile.
 */
export const canAccessCcoTestPage: CanActivateFn = (route, state) => {
  if (!environment.enableCcoTestPage) {
    inject(Router).navigate(["create-request"]);
    return false;
  }

  return hasAnyRole("IsAdmin")(route, state);
};
